package com.karyadina.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Smartphone
import androidx.compose.material.icons.filled.AutoAwesome
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.outlined.LightMode
import androidx.compose.material.icons.outlined.DarkMode
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.karyadina.ui.navigation.DashboardPreferences

import org.jetbrains.compose.resources.stringResource

import androidx.compose.ui.platform.LocalUriHandler

@Composable
fun SettingsDialog(
    preferences: DashboardPreferences,
    onToggleDailyFocus: (Boolean) -> Unit,
    onToggleWeeklyReview: (Boolean) -> Unit,
    onToggleShowAllTools: (Boolean) -> Unit,
    onToggleAlwaysShowDocTools: (Boolean) -> Unit,
    onSetThemeMode: (com.karyadina.model.ThemeMode) -> Unit,
    onSetCustomTheme: (com.karyadina.model.AppThemeColor?) -> Unit,
    onSetLanguage: (String) -> Unit,
    onResetPersona: () -> Unit,
    onDismiss: () -> Unit
) {
    val uriHandler = LocalUriHandler.current
    Dialog(onDismissRequest = onDismiss) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = 6.dp
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(24.dp)
            ) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        stringResource(com.karyadina.ui.theme.StudyStrings.SettingsTitle),
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, stringResource(com.karyadina.ui.theme.StudyStrings.ActionClose))
                    }
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                Text(
                    stringResource(com.karyadina.ui.theme.StudyStrings.SettingsCustomizeHome),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.primary
                )
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Toggle Daily Focus
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsDailyFocusTitle), style = MaterialTheme.typography.bodyLarge)
                        Text(
                            stringResource(com.karyadina.ui.theme.StudyStrings.SettingsDailyFocusDesc),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    Switch(
                        checked = preferences.showDailyFocus,
                        onCheckedChange = onToggleDailyFocus
                    )
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Toggle Weekly Review
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsWeeklyReviewTitle), style = MaterialTheme.typography.bodyLarge)
                        Text(
                            stringResource(com.karyadina.ui.theme.StudyStrings.SettingsWeeklyReviewDesc),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    Switch(
                        checked = preferences.showWeeklyReview,
                        onCheckedChange = onToggleWeeklyReview
                    )
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Toggle Show All Tools
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsShowAllTitle), style = MaterialTheme.typography.bodyLarge)
                        Text(
                            stringResource(com.karyadina.ui.theme.StudyStrings.SettingsShowAllDesc),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    Switch(
                        checked = preferences.showAllTools,
                        onCheckedChange = onToggleShowAllTools
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))
                
                // Toggle Always Show Doc Tools
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsDocToolsTitle), style = MaterialTheme.typography.bodyLarge)
                        Text(
                            stringResource(com.karyadina.ui.theme.StudyStrings.SettingsDocToolsDesc),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    Switch(
                        checked = preferences.alwaysShowDocTools,
                        onCheckedChange = onToggleAlwaysShowDocTools
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))
                HorizontalDivider()
                Spacer(modifier = Modifier.height(24.dp))

                // CloudSync Settings (Injected via CompositionLocal)
                com.karyadina.ui.screens.cloudsync.LocalCloudSyncSettingsProvider.current.invoke()

                Spacer(modifier = Modifier.height(24.dp))
                HorizontalDivider()
                Spacer(modifier = Modifier.height(24.dp))
                
                // Theme Mode
                Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsThemeMode), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(12.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    val modes = com.karyadina.model.ThemeMode.entries
                    modes.forEach { mode ->
                        val isSelected = preferences.themeMode == mode
                        val icon = when (mode) {
                            com.karyadina.model.ThemeMode.LIGHT -> Icons.Outlined.LightMode
                            com.karyadina.model.ThemeMode.DARK -> Icons.Outlined.DarkMode
                            com.karyadina.model.ThemeMode.SYSTEM -> Icons.Default.Smartphone
                        }
                        
                        OutlinedCard(
                            modifier = Modifier
                                .weight(1f)
                                .height(80.dp)
                                .clickable { onSetThemeMode(mode) },
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = if (isSelected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface,
                                contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurface
                            ),
                            border = if (isSelected) 
                                androidx.compose.foundation.BorderStroke(2.dp, MaterialTheme.colorScheme.primary) 
                            else 
                                CardDefaults.outlinedCardBorder()
                        ) {
                            Column(
                                modifier = Modifier.fillMaxSize(),
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Icon(icon, contentDescription = null)
                                Spacer(Modifier.height(4.dp))
                                Text(
                                    getThemeModeName(mode),
                                    style = MaterialTheme.typography.labelMedium,
                                    fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
                                )
                            }
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(24.dp))
                
                // App Color
                Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsAppColor), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(12.dp))
                
                // Manual Colors
                @OptIn(ExperimentalLayoutApi::class)
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                     val themeColors = com.karyadina.model.AppThemeColor.entries
                     
                     // Reset / Auto (Dynamic)
                     Column(horizontalAlignment = Alignment.CenterHorizontally) {
                         Box(
                            modifier = Modifier
                                .size(48.dp) // Larger touch target
                                .clip(CircleShape)
                                .background(MaterialTheme.colorScheme.surfaceVariant)
                                .clickable { onSetCustomTheme(null) }
                                .border(
                                    width = if (preferences.customTheme == null) 3.dp else 1.dp,
                                    color = if (preferences.customTheme == null) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant,
                                    shape = CircleShape
                                ),
                             contentAlignment = Alignment.Center
                         ) {
                             if (preferences.customTheme == null) {
                                 Icon(Icons.Default.AutoAwesome, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(24.dp))
                             } else {
                                 Text("A", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                             }
                         }
                         Spacer(Modifier.height(4.dp))
                         Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsAuto), style = MaterialTheme.typography.labelSmall)
                     }
                     
                     themeColors.forEach { theme ->
                         val isSelected = preferences.customTheme == theme
                         Column(horizontalAlignment = Alignment.CenterHorizontally) {
                             Box(
                                modifier = Modifier
                                    .size(48.dp)
                                    .clip(CircleShape)
                                    .background(theme.color)
                                    .clickable { onSetCustomTheme(theme) }
                                    .border(
                                        width = if (isSelected) 3.dp else 0.dp,
                                        color = MaterialTheme.colorScheme.onSurface,
                                        shape = CircleShape
                                    ),
                                contentAlignment = Alignment.Center
                             ) {
                                 if (isSelected) {
                                     Icon(
                                         Icons.Default.Check, 
                                         contentDescription = null,
                                         tint = Color.White, // Always white check on colored bg
                                         modifier = Modifier.size(24.dp)
                                     )
                                 }
                             }
                             Spacer(Modifier.height(4.dp))
                             Text(getAppColorName(theme), style = MaterialTheme.typography.labelSmall)
                         }
                     }
                }

                Spacer(modifier = Modifier.height(24.dp))
                
                // Language Settings
                Text(org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.SettingsLanguageTitle), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(12.dp))
                
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    val languages = listOf(
                        "en" to com.karyadina.ui.theme.StudyStrings.LangEnglish,
                        "hi" to com.karyadina.ui.theme.StudyStrings.LangHindi
                    )
                    
                    languages.forEach { (code, labelRes) ->
                        val isSelected = preferences.language == code
                        
                        OutlinedCard(
                            modifier = Modifier
                                .weight(1f)
                                .height(60.dp)
                                .clickable { onSetLanguage(code) },
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = if (isSelected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface
                            ),
                            border = if (isSelected) androidx.compose.foundation.BorderStroke(2.dp, MaterialTheme.colorScheme.primary) else CardDefaults.outlinedCardBorder()
                        ) {
                             Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                 Text(
                                     org.jetbrains.compose.resources.stringResource(labelRes),
                                     style = MaterialTheme.typography.labelLarge,
                                     fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
                                 )
                             }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))
                
                TextButton(
                    onClick = onResetPersona,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.primary)
                ) {
                    Text(stringResource(com.karyadina.ui.theme.StudyStrings.SettingsChangePersona))
                }
                
                Spacer(modifier = Modifier.height(24.dp))
                
                Button(
                    onClick = onDismiss,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(stringResource(com.karyadina.ui.theme.StudyStrings.ActionDone))
                }
            }
        }
    }
}

// Helpers for localized names
@Composable
fun getThemeModeName(mode: com.karyadina.model.ThemeMode): String {
    return when (mode) {
        com.karyadina.model.ThemeMode.SYSTEM -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ThemeSystem)
        com.karyadina.model.ThemeMode.LIGHT -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ThemeLight)
        com.karyadina.model.ThemeMode.DARK -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ThemeDark)
    }
}

@Composable
fun getAppColorName(color: com.karyadina.model.AppThemeColor): String {
    return when (color) {
        com.karyadina.model.AppThemeColor.GREEN -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ColorGreen)
        com.karyadina.model.AppThemeColor.BLUE -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ColorBlue)
        com.karyadina.model.AppThemeColor.ORANGE -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ColorOrange)
        com.karyadina.model.AppThemeColor.PURPLE -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ColorPurple)
        com.karyadina.model.AppThemeColor.CYAN -> org.jetbrains.compose.resources.stringResource(com.karyadina.ui.theme.StudyStrings.ColorCyan)
    }
}
